"use client";

import React, { useEffect, useRef } from "react";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import * as THREE from "three";
import { GLTFLoader, GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { textureFromCanvas } from "../textures";

gsap.registerPlugin(ScrollTrigger);

export default function Global3DBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mixerRef = useRef<THREE.AnimationMixer | null>(null);
  const wireframeMixerRef = useRef<THREE.AnimationMixer | null>(null);
  const mainGroupRef = useRef<THREE.Group | null>(null);
  const glowSpriteRef = useRef<THREE.Sprite | null>(null);
  const codeMaterialRef = useRef<THREE.ShaderMaterial | null>(null);
  const baseYRef = useRef(0);
  const isWireframeRef = useRef(false);

  // MOUSE INERTIA STATE
  const mouseLag = useRef({
    target: new THREE.Vector2(0.5, 0.5),
    current: new THREE.Vector2(0.5, 0.5)
  });

  const backgroundUniforms = useRef({
    uTime: { value: 0 },
    uMouse: { value: new THREE.Vector2(0.5, 0.5) },
    uResolution: { value: new THREE.Vector2(typeof window !== 'undefined' ? window.innerWidth : 0, typeof window !== 'undefined' ? window.innerHeight : 0) },
    uCenterLift: { value: 0 },
    uSaturation: { value: 1.0 }
  }).current;

  const getStableHeight = () => {
    if (typeof window === "undefined") return 0;
  
    return window.visualViewport?.height ?? window.innerHeight;
  };
  
  
  useEffect(() => {
    if (!canvasRef.current) return;
    const STABLE_HEIGHT = getStableHeight();
    // --- 1. SETUP & SCENE ---
    const isMobile = window.innerWidth < 768;
    backgroundUniforms.uCenterLift.value = isMobile ? 1.0 : 0.0;
    backgroundUniforms.uSaturation.value = isMobile ? 1.35 : 1.2; 
    
    // Config
    const ROT_MULT = isMobile ? 0.4 : 0.8;
    const MOVE_MULT = isMobile ? 0.45 : 0.9;
    const Z_MULT = isMobile ? 0.35 : 0.9;
    
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x000205, isMobile ? 0.015 : 0.028);

    const sizes = {
      width: window.innerWidth,
      height: isMobile ? STABLE_HEIGHT : window.innerHeight
    };
    const camera = new THREE.PerspectiveCamera(35, sizes.width / sizes.height, 0.1, 100);
    camera.position.set(0, 0, 10);
    scene.add(camera);

    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setSize(sizes.width, sizes.height);
    const DPR = isMobile ? 1 : Math.min(window.devicePixelRatio, 2);
    renderer.setPixelRatio(DPR);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = isMobile ? 1.2 : 1.5;

    // --- 2. LIGHTING ---
    const ambient = new THREE.AmbientLight(0xffffff, isMobile ? 0.5 : 0.3);
    scene.add(ambient);

    const keyLight = new THREE.DirectionalLight(0xffffff, 2.2);
    keyLight.position.set(5, 5, 5);
    scene.add(keyLight);

    if (isMobile) {
      const mobileFill = new THREE.DirectionalLight(0x4444ff, 1.0);
      mobileFill.position.set(-3, 2, 4);
      scene.add(mobileFill);
    }

    const rimLightA = new THREE.SpotLight(0x00f2ff, isMobile ? 60 : 120);
    rimLightA.position.set(-5, 5, 5);
    rimLightA.lookAt(0, 0, 0);
    scene.add(rimLightA);

    const rimLightB = new THREE.SpotLight(0x0044ff, isMobile ? 40 : 90);
    rimLightB.position.set(5, -2, 5);
    rimLightB.lookAt(0, 0, 0);
    scene.add(rimLightB);

    // --- 3. POST-PROCESSING ---
    const composer = new EffectComposer(renderer);
    composer.setPixelRatio(DPR);
    composer.addPass(new RenderPass(scene, camera));

    let bloomPass: UnrealBloomPass | null = null;

    if (!isMobile) {
      bloomPass = new UnrealBloomPass(
        new THREE.Vector2(sizes.width, sizes.height),
        2.2, 0.4, 0.85
      );
      composer.addPass(bloomPass);
    }

    // --- 4. BACKGROUND SHADER ---
    const bgGeometry = new THREE.PlaneGeometry(40, 40);
    const bgMaterial = new THREE.ShaderMaterial({
      uniforms: backgroundUniforms,
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float uTime;
        uniform vec2 uMouse;
        uniform vec2 uResolution;
        uniform float uCenterLift;
        uniform float uSaturation;
        varying vec2 vUv;

        void main() {
            vec2 st = vUv;
            st.x *= uResolution.x / uResolution.y;
            vec2 mouse = uMouse;
            mouse.x *= uResolution.x / uResolution.y;

            vec3 base = vec3(0.002, 0.005, 0.015);
            float center = smoothstep(0.7, 0.0, distance(vUv, vec2(0.5)));
            vec3 color = base + vec3(0.01, 0.02, 0.05) * center * uCenterLift;

            vec2 pos1 = vec2(0.5, 0.5) + vec2(sin(uTime * 0.15) * 0.3, cos(uTime * 0.1) * 0.2);
            pos1.x *= uResolution.x / uResolution.y;
            float dist1 = distance(st, pos1);
            color += vec3(0.0, 0.5, 1.0) * (0.02 * exp(-dist1 * 3.5)); 

            vec2 pos2 = vec2(0.5, 0.5) + vec2(cos(uTime * 0.1) * 0.4, sin(uTime * 0.2) * 0.3);
            pos2.x *= uResolution.x / uResolution.y;
            float dist2 = distance(st, pos2);
            color += vec3(0.5, 0.0, 1.0) * (0.015 * exp(-dist2 * 4.0)); 

            float distMouse = distance(st, mouse);
            color += vec3(0.2, 0.6, 1.0) * (0.005 * exp(-distMouse * 3.0));

            float vignette = 1.0 - smoothstep(0.35, 0.9, length(vUv - 0.5) * 1.35);
            color *= vignette;
            
            float luma = dot(color, vec3(0.2126, 0.7152, 0.0722));
            color = mix(vec3(luma), color, uSaturation);

            gl_FragColor = vec4(color, 1.0);
        }
      `,
      depthWrite: false,
    });
    const bgMesh = new THREE.Mesh(bgGeometry, bgMaterial);
    bgMesh.position.z = -10;
    scene.add(bgMesh);

    // --- ELECTRIC HALO GLOW ---
    const glowCanvas = document.createElement("canvas");
    glowCanvas.width = 256;
    glowCanvas.height = 256;
    const glowCtx = glowCanvas.getContext("2d", { willReadFrequently: true });
    if (glowCtx) {
      const gradient = glowCtx.createRadialGradient(128, 128, 0, 128, 128, 128);
      gradient.addColorStop(0, "rgba(255,255,255,1)");
      gradient.addColorStop(0.12, "rgba(34,211,238,0.9)");
      gradient.addColorStop(0.35, "rgba(0,100,255,0.35)");
      gradient.addColorStop(0.6, "rgba(0,30,150,0.1)");
      gradient.addColorStop(1, "rgba(0,0,0,0)");
      glowCtx.fillStyle = gradient;
      glowCtx.fillRect(0, 0, 256, 256);
    }
    const glowTexture = textureFromCanvas(glowCanvas, glowCtx);
    const glowMaterial = new THREE.SpriteMaterial({
      map: glowTexture,
      transparent: true,
      opacity: isMobile ? 1.1 : 0.8,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    const glowSprite = new THREE.Sprite(glowMaterial);
    glowSprite.position.set(0, 0, isMobile ? -2.2 : -4);
    glowSprite.scale.set(isMobile ? 8 : 6.5, isMobile ? 8 : 6.5, 1);
    scene.add(glowSprite);
    glowSpriteRef.current = glowSprite;

    // --- 5. CODE PARTICLES ---
    const chars = ['0','1','2','3','4','5','6','7','8','9','A','B','C','D','E','F','X','[',']','<','>',':','/','G','P','U'];
    const charSize = 128; 
    const atlasCanvas = document.createElement('canvas');
    atlasCanvas.width = charSize * chars.length;
    atlasCanvas.height = charSize;
    const atlasCtx = atlasCanvas.getContext('2d', { willReadFrequently: true });
    if (atlasCtx) {
      atlasCtx.font = 'bold 80px monospace';
      atlasCtx.textAlign = 'center';
      atlasCtx.textBaseline = 'middle';
      atlasCtx.fillStyle = '#ffffff';
      chars.forEach((char, i) => atlasCtx.fillText(char, (i * charSize) + (charSize/2), charSize/2));
    }
    // Linear filtering, clamped wrapping and no mipmaps come with the helper.
    const codeAtlas = textureFromCanvas(atlasCanvas, atlasCtx);


    const particleCount = isMobile ? 20 : 50;
    const codeGeometry = new THREE.BufferGeometry();
    const codePositions = new Float32Array(particleCount * 3);
    const codeRandoms = new Float32Array(particleCount); 
    for (let i = 0; i < particleCount; i++) {
      const radius = isMobile ? 2.5 : 3.5;
      const angle = Math.random() * Math.PI * 2;
      const height = (Math.random() - 0.5) * 2;
      codePositions[i*3]     = Math.cos(angle) * radius;
      codePositions[i*3 + 1] = height;
      codePositions[i*3 + 2] = Math.sin(angle) * radius;
      codeRandoms[i] = Math.random();
    }
    codeGeometry.setAttribute('position', new THREE.BufferAttribute(codePositions, 3));
    codeGeometry.setAttribute('aRandom', new THREE.BufferAttribute(codeRandoms, 1));
    const codeMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uTexture: { value: codeAtlas },
        uAtlasSize: { value: chars.length },
        uStringLen: { value: 10.0 }, 
        uOpacity: { value: isMobile ? 0.06 : 0.1 }, 
        uSize: { value: isMobile ? 500.0 : 900.0 } 
      },
      vertexShader: `
        attribute float aRandom;
        varying float vRandom;
        uniform float uSize;
        void main() {
            vRandom = aRandom;
            vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
            gl_PointSize = uSize / -mvPosition.z; 
            gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform sampler2D uTexture;
        uniform float uTime;
        uniform float uAtlasSize;
        uniform float uStringLen;
        uniform float uOpacity;
        varying float vRandom;
        void main() {
            float yMin = 0.42; float yMax = 0.58;
            if (gl_PointCoord.y < yMin || gl_PointCoord.y > yMax) discard;
            float charIndex = floor(gl_PointCoord.x * uStringLen);
            float localUvX = fract(gl_PointCoord.x * uStringLen);
            // gl_PointCoord runs top-down and so does the atlas buffer - no flip.
            float localUvY = (gl_PointCoord.y - yMin) / (yMax - yMin);
            float changeSpeed = 4.0; 
            float timeStep = floor(uTime * changeSpeed + vRandom * 100.0);
            float charFrame = floor(mod(timeStep, uAtlasSize));
            vec2 atlasUV = vec2((charFrame + localUvX) / uAtlasSize, localUvY);
            vec4 texColor = texture2D(uTexture, atlasUV);
            float edgeFade = (charIndex < 1.0 || charIndex > uStringLen - 2.0) ? 0.5 : 1.0; 
            gl_FragColor = vec4(vec3(0.25, 1.1, 1.4) * texColor.rgb, texColor.a * uOpacity * edgeFade);
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    codeMaterialRef.current = codeMaterial;
    const codeSystem = new THREE.Points(codeGeometry, codeMaterial);

    // --- 6. MODEL LOADER (OPTIMIZED: DEFERRED LOADING) ---
    const loader = new GLTFLoader();
    loader.setMeshoptDecoder(MeshoptDecoder);
    
    const gpuPath = isMobile ? "/GPU_mobile.glb" : "/GPU.glb";

    loader.load(gpuPath, (gltf: GLTF) => {
      const model = gltf.scene;
      
      const box = new THREE.Box3().setFromObject(model);
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const targetSize = isMobile ? 3.5 : 6;
      const targetScale = targetSize / maxDim;
      const center = box.getCenter(new THREE.Vector3());
      model.position.set(-center.x, -center.y, -center.z);
      
      const mainGroup = new THREE.Group();
      mainGroup.scale.setScalar(targetScale);
      mainGroup.add(codeSystem);
      codeSystem.position.set(0, 0, 0);
      
      // A. SOLID MESH (Immediate)
      const solidGroup = new THREE.Group();
      const solidModel = model.clone();
      
      solidModel.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          const m = (child as THREE.Mesh).material as THREE.MeshStandardMaterial;
          if (isMobile) {
            m.normalMap = null;
            if (m.normalScale) m.normalScale.set(0, 0);
          }
          m.metalness = 1.0;
          m.roughness = Math.min(m.roughness + (isMobile ? 0.1 : 0), 1);
          m.envMapIntensity = isMobile ? 2.2 : 1.0;
          m.transparent = false;
          m.opacity = 1.0;
          m.needsUpdate = true;
        }
      });
      
      solidModel.rotation.z -= Math.PI; 
      solidGroup.add(solidModel);
      mainGroup.add(solidGroup);

      scene.add(mainGroup);
      mainGroupRef.current = mainGroup;
      baseYRef.current = mainGroup.position.y;

      renderer.compile(scene, camera);

      if (gltf.animations.length > 0) {
        mixerRef.current = new THREE.AnimationMixer(solidModel);
        gltf.animations.forEach(clip => mixerRef.current?.clipAction(clip).play());
      }

      // B. WIREFRAME MESH (Deferred)
      setTimeout(() => {
          const wireframeGroup = new THREE.Group();
          const wireframeModel = model.clone();

          wireframeModel.traverse((child) => {
            if ((child as THREE.Mesh).isMesh) {
              const mesh = child as THREE.Mesh;
              const edges = new THREE.EdgesGeometry(mesh.geometry, 15); 
              const lineMaterial = new THREE.LineBasicMaterial({
                color: 0x00aaff,
                transparent: true,
                opacity: 0, 
                blending: THREE.AdditiveBlending,
                depthWrite: false
              });
              const wireframe = new THREE.LineSegments(edges, lineMaterial);
              wireframe.renderOrder = 10;
              mesh.add(wireframe);

              mesh.material = new THREE.MeshBasicMaterial({
                color: 0x003366,
                wireframe: true,
                transparent: true,
                opacity: 0, 
                blending: THREE.AdditiveBlending,
                depthWrite: false
              });
            }
          });
          
          wireframeModel.rotation.z -= Math.PI;
          wireframeGroup.add(wireframeModel);
          mainGroup.add(wireframeGroup);

          if (gltf.animations.length > 0) {
            wireframeMixerRef.current = new THREE.AnimationMixer(wireframeModel);
            gltf.animations.forEach(clip => wireframeMixerRef.current?.clipAction(clip).play());
          }
      }, 100);

      // --- 7. GSAP TIMELINE ---
      const horizontalOffset = isMobile ? 1.2 : 3.5;
      const finalScale = isMobile ? 1.2 : 1.8;

      const triggerEl = document.getElementById("scrolly-container");

      if (!triggerEl) {
        console.warn("ScrollTrigger skipped: #scrolly-container not found");
        return;
      }

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: "#scrolly-container",
          start: "top top",
          end: "bottom bottom",
          scrub: 1,
          ignoreMobileResize: true,
        } as any,
      });

      // 1. Rotate & Move Right
      tl.to(mainGroup.rotation, { y: Math.PI * 0.5 * ROT_MULT, duration: 2 }, 0);
      tl.to(mainGroup.position, { x: horizontalOffset * MOVE_MULT, duration: 3 }, 2);
      tl.to(mainGroup.rotation, { x: 0.2 * ROT_MULT, y: Math.PI * 1.2 * ROT_MULT, duration: 3 }, 2);

      // 2. Move Left & Tilt
      tl.to(mainGroup.position, { x: -horizontalOffset * MOVE_MULT, z: 2 * Z_MULT, duration: 3 }, 5);
      tl.to(mainGroup.rotation, { x: -0.2 * ROT_MULT, y: Math.PI * 2.2 * ROT_MULT, z: 0.5 * ROT_MULT, duration: 3 }, 5);

      // 3. Return Center
      tl.to(mainGroup.position, { x: 0, y: 0, z: -2 * Z_MULT, duration: 2 }, 8);
      tl.to(mainGroup.rotation, { x: 0, y: Math.PI * 4 * ROT_MULT, z: 0, duration: 2 }, 8);
      
      // 4. PREPARE FOR WIREFRAME
      tl.to(mainGroup.position, { x: 0, y: 0, z: 0, duration: 3 }, 11);
      tl.to(mainGroup.rotation, { x: 0, y: Math.PI / 16, z: 0, duration: 3 }, 11);
      tl.to(mainGroup.scale, { x: targetScale * finalScale, y: targetScale * finalScale, z: targetScale * finalScale, duration: 3 }, 11);

      // 5. THE TRANSITION
      const transitionObj = { val: 0 };

      tl.to(transitionObj, {
        val: 1,
        duration: 2,
        onStart: () => {
          isWireframeRef.current = true;
          solidModel.traverse((child) => {
            if ((child as THREE.Mesh).isMesh) {
              const m = (child as THREE.Mesh).material as THREE.Material;
              m.transparent = true;
              m.needsUpdate = true;
            }
          });
        },
        onUpdate: () => {
          // A. Fade Out Solid
          const solidOpacity = 1.0 - transitionObj.val;
          if (glowSpriteRef.current) {
             glowSpriteRef.current.material.opacity = THREE.MathUtils.lerp(isMobile ? 0.6 : 0.4, 0, transitionObj.val);
          }
          solidModel.traverse((child) => {
            if ((child as THREE.Mesh).isMesh) {
              ((child as THREE.Mesh).material as THREE.Material).opacity = solidOpacity;
            }
          });

          // B. Fade In Wireframe
          if(mainGroup.children.length > 2) { 
            const wfGroup = mainGroup.children[2];
            if(wfGroup) {
              wfGroup.traverse((child) => {
                if ((child as THREE.Mesh).isMesh) {
                  const mesh = child as THREE.Mesh;
          
                  // Only update opacity if material exists
                  if(mesh.material && mesh.material instanceof THREE.MeshBasicMaterial){
                    mesh.material.opacity = transitionObj.val * 0.2;
                  }
          
                  // Only update line segments if they exist
                  if(mesh.children && mesh.children.length > 0){
                    mesh.children.forEach((c) => {
                      if (c instanceof THREE.LineSegments && c.material){
                        (c.material as THREE.LineBasicMaterial).opacity = transitionObj.val * 0.8;
                      }
                    });
                  }
                }
              });
            }
          }          

          // C. Bloom
          if (bloomPass) {
            bloomPass.strength = THREE.MathUtils.lerp(1.2, 2.0, transitionObj.val);
          }
        }
      }, 11.5);

      // --- 6. WIREFRAME HOLD ---
      tl.to({}, { duration: 2.5 }, 13.5); // HOLD wireframe ~2.5s

      // --- 7. EXIT ANIMATION ---
      if (canvasRef.current) {
        tl.to(
          canvasRef.current,
          {
            opacity: 0,
            duration: 2.0,
            ease: "power2.inOut",
          },
          16 // starts AFTER the hold
        );
      }

      ScrollTrigger.refresh();
      composer.render();
    });

    // --- 8. RENDER LOOP ---
    const clock = new THREE.Clock();
    let lastFrameTime = 0;
    const MOBILE_FPS = 30;

    // The loop has to be stoppable. Cleanup disposes the textures and the
    // renderer, and a frame that lands after that makes three re-upload every
    // texture into GL objects it still holds - which Safari reports, once per
    // texture, as "glTexStorage2D: Texture is immutable". Left running, the loop
    // also keeps rendering a dead scene for the rest of the tab's life.
    let frameId = 0;
    let disposed = false;

    const onMouseMove = (event: MouseEvent) => {
      mouseLag.current.target.x = event.clientX / window.innerWidth;
      mouseLag.current.target.y = 1.0 - (event.clientY / window.innerHeight);
    };
    if (!isMobile) window.addEventListener("mousemove", onMouseMove);

    const animate = (time = 0) => {
      if (disposed) return;

      if (isMobile) {
         const delta = time - lastFrameTime;
         if (delta < 1000 / MOBILE_FPS) { frameId = requestAnimationFrame(animate); return; }
         lastFrameTime = time;
      }
      
      const delta = clock.getDelta();
      const elapsedTime = clock.getElapsedTime();

      // OPTIMIZATION: Stop rendering if canvas is invisible (opacity 0)
      if(canvasRef.current && canvasRef.current.style.opacity === '0') {
         frameId = requestAnimationFrame(animate);
         return;
      }

      if (codeSystem) {
        const t = elapsedTime;
        codeSystem.rotation.y = t * 0.15;
        codeSystem.rotation.z = Math.sin(t * 0.4) * 0.15;
        codeSystem.position.y = Math.sin(t * 1.2) * 0.12;
        codeSystem.position.x = Math.cos(t * 0.8) * 0.15;
      }

      if (!isWireframeRef.current) {
        if (isMobile) {
          rimLightA.intensity = 20;
          rimLightB.intensity = 25;
        } else {
          rimLightB.intensity = 65 + Math.sin(elapsedTime * 2) * 20;
          rimLightA.intensity = 55 + Math.cos(elapsedTime * 1.8) * 10;
        }
      } else {
        rimLightA.intensity = isMobile ? 12 : 18;
        rimLightB.intensity = isMobile ? 14 : 22;
      }  
      
      mouseLag.current.current.lerp(mouseLag.current.target, 0.05);
      backgroundUniforms.uMouse.value.copy(mouseLag.current.current);
      if (!isMobile) {
        backgroundUniforms.uTime.value = elapsedTime;
      }
      if (codeMaterialRef.current) codeMaterialRef.current.uniforms.uTime.value = elapsedTime;
      if (mixerRef.current) mixerRef.current.update(delta);
      if (wireframeMixerRef.current) wireframeMixerRef.current.update(delta);

      if (mainGroupRef.current) {
        mainGroupRef.current.position.y = baseYRef.current + Math.sin(elapsedTime * 0.5) * 0.15;
      }

      if (glowSpriteRef.current && mainGroupRef.current) {
        glowSpriteRef.current.position.set(
          mainGroupRef.current.position.x,
          mainGroupRef.current.position.y,
          mainGroupRef.current.position.z - (isMobile ? 1 : 2)
        );
        const baseScale = isMobile ? 4 : 6.5;
        const pulse = Math.sin(elapsedTime * 0.8) * 0.25;
        const finalScale = baseScale * mainGroupRef.current.scale.x + pulse;
        glowSpriteRef.current.scale.set(finalScale, finalScale, 1);
      }    

      composer.render();
      frameId = requestAnimationFrame(animate);
    };
    animate();

    const handleResize = () => {
      sizes.width = window.innerWidth;
      sizes.height = isMobile ? STABLE_HEIGHT : window.innerHeight;

      camera.aspect = sizes.width / sizes.height;
      camera.updateProjectionMatrix();

      renderer.setSize(sizes.width, sizes.height);
      composer.setSize(sizes.width, sizes.height);

      backgroundUniforms.uResolution.value.set(
        sizes.width,
        sizes.height
      );
    };
    window.addEventListener("resize", handleResize);

    handleResize(); 
    setTimeout(handleResize, 50); 

    return () => {
      // Order matters: the loop stops before anything it renders is disposed.
      disposed = true;
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", handleResize);
      if (!isMobile) window.removeEventListener("mousemove", onMouseMove);
      renderer.dispose();
      renderer.forceContextLoss();
      bgGeometry.dispose();
      bgMaterial.dispose();
      codeGeometry.dispose();
      codeMaterial.dispose();
      codeAtlas.dispose();
      glowTexture.dispose();
      glowMaterial.dispose();
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, []);

  return <canvas ref={canvasRef} className="fixed inset-0 z-0 pointer-events-none" style={{ opacity: 1 }} />;
}